/**
 * Панель решений игрока. Три раздела с единой структурой (шапка: заголовок + состояние):
 *   «Прогноз» — направление и число пунктов;
 *   «Ставка» — строка состояния хода, сумма, действия;
 *   «Предварительное действие» — одноразовое действие на ближайший ход.
 * Горячие клавиши: ↑/↓ — направление, +/− — пункты (Shift — ×4), F — пас, C — чек/колл,
 * B — ставка/райз, A — олл-ин. Перед пасом и олл-ином — подтверждение.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AllowedActions, AutoMode, GameView } from '@mt/protocol';
import { client, useApp } from '../net/client';
import { cr, pts, secondsLeft } from '../lib/format';
import { updateSettings, useSettings } from '../lib/settings';
import { Dialog } from './Dialog';
import { TurnBar } from './Seats';

const toMinor = (credits: number) => Math.round(credits * 100);
const mmss = (s: number | null) => (s === null ? '—' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);
const ROUND_OF: Partial<Record<GameView['stage'], number>> = {
  round1: 1,
  round2: 2,
  round3: 3,
};
const NEXT_REVEAL: Record<number, string> = {
  1: 'вместе со шкалой цен',
  2: 'вместе с составом акций',
};

export function Controls({ game, now }: { game: GameView; now: number }) {
  const me = game.me;
  const tick = game.tick;
  const settings = useSettings();
  const pending = useApp((s) => s.pending.size > 0);
  const [pointsDraft, setPointsDraft] = useState<string>('');
  const [amount, setAmount] = useState<number>(0);
  const [confirm, setConfirm] = useState<null | 'fold' | 'allin'>(null);
  const [allinPoints, setAllinPoints] = useState('');
  const a = me.allowed;
  const locked = !['prep', 'round1', 'round2', 'round3'].includes(game.stage);
  const meSeat = game.seats.find((s) => s.isMe);
  const folded = meSeat?.status === 'folded';
  const allIn = meSeat?.status === 'allin';
  const canEditForecast = !locked && !folded;
  // направление выбирается в подготовке и кругах 1–2; в круге 3 меняются только пункты
  const canEditDirection = canEditForecast && game.stage !== 'round3';

  useEffect(() => setPointsDraft(me.forecast.points === null ? '' : pts(me.forecast.points)), [me.forecast.points]);

  // сумма ставки/райза по умолчанию — минимальная
  const range = a?.bet ?? a?.raise ?? null;
  useEffect(() => {
    if (range) setAmount(range.min);
  }, [range?.min, range?.max, game.stage]);

  const sendPoints = (v: number | null) => {
    if (v !== null && (v <= 0 || Math.abs(v / tick - Math.round(v / tick)) > 1e-9)) {
      client.toast('error', `Число пунктов — положительное и кратное ${pts(tick)}`);
      return;
    }
    client.forecast({ points: v });
  };
  const stepPoints = (d: number) => {
    const cur = me.forecast.points ?? 0;
    sendPoints(Math.max(tick, Math.round((cur + d) / tick) * tick));
  };
  const commitDraft = () => {
    const raw = pointsDraft.trim();
    if (raw === '') return me.forecast.points !== null && sendPoints(null);
    const v = Number(raw.replace(',', '.'));
    if (!Number.isFinite(v)) return client.toast('error', 'Введите число пунктов');
    if (v !== me.forecast.points) sendPoints(v);
  };

  const doFold = () => (settings.confirmFold ? setConfirm('fold') : client.act('fold'));
  const doAllIn = () => {
    setAllinPoints(me.forecast.points === null ? '' : pts(me.forecast.points));
    setConfirm('allin');
  };
  const doCheckCall = () => {
    if (a?.check) client.act('check');
    else if (a?.call) {
      if (a.call.allIn) doAllIn();
      else client.act('call');
    }
  };
  const doBetRaise = () => {
    if (!range) return;
    const v = Math.min(Math.max(amount, range.min), range.max);
    if (v - (meSeat?.roundBet ?? 0) >= (meSeat?.stack ?? 0)) return doAllIn();
    client.act(a?.bet ? 'bet' : 'raise', v);
  };

  // горячие клавиши (латиница и русская раскладка)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || confirm) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (canEditDirection) {
        if (e.key === 'ArrowUp') (e.preventDefault(), client.forecast({ direction: 'up' }));
        if (e.key === 'ArrowDown') (e.preventDefault(), client.forecast({ direction: 'down' }));
      }
      if (canEditForecast) {
        if (e.key === '+' || e.key === '=') stepPoints(e.shiftKey ? 4 * tick : tick);
        if (e.key === '-' || e.key === '_') stepPoints(e.shiftKey ? -4 * tick : -tick);
      }
      if (!a) return;
      if (k === 'f' || k === 'а') doFold();
      if (k === 'c' || k === 'с') doCheckCall();
      if (k === 'b' || k === 'и' || k === 'r' || k === 'к') doBetRaise();
      if ((k === 'a' || k === 'ф') && a.allIn) doAllIn();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const lockLeft = secondsLeft(game.lockAt, now);
  const pointsRequired = game.stage === 'round3';
  const missingPoints = canEditForecast && me.forecast.points === null;
  const missingDir = canEditForecast && me.forecast.direction === null;

  if (!me.participant) {
    return (
      <section className="panel controls">
        <p className="muted-line">Вы наблюдаете за розыгрышем. Участвовать можно со следующего розыгрыша.</p>
      </section>
    );
  }

  return (
    <section className="panel controls" aria-label="Ваш прогноз и действия">
      {/* ── Прогноз ── */}
      <div className="ctl">
        <CtlHead title="Прогноз">
          {locked ? (
            <span className="state">зафиксирован</span>
          ) : folded ? (
            <span className="state">вы спасовали</span>
          ) : pointsRequired ? (
            <span className="state warn">фиксация через {mmss(lockLeft)}</span>
          ) : null}
        </CtlHead>
        <div className="dir-toggle" role="radiogroup" aria-label="Направление">
          <button
            role="radio"
            aria-checked={me.forecast.direction === 'up'}
            className={`dir up ${me.forecast.direction === 'up' ? 'on' : ''}`}
            disabled={!canEditDirection}
            onClick={() => client.forecast({ direction: 'up' })}
          >
            <span>▲ Выше</span>
            <kbd>↑</kbd>
          </button>
          <button
            role="radio"
            aria-checked={me.forecast.direction === 'down'}
            className={`dir down ${me.forecast.direction === 'down' ? 'on' : ''}`}
            disabled={!canEditDirection}
            onClick={() => client.forecast({ direction: 'down' })}
          >
            <span>▼ Ниже</span>
            <kbd>↓</kbd>
          </button>
        </div>
        <div className={`field-row ${pointsRequired && missingPoints ? 'invalid' : ''}`}>
          <label htmlFor="pts">
            <span>Пункты</span>
            <small>{pointsRequired ? 'обязательно до фиксации' : 'предварительно, можно позже'}</small>
          </label>
          <div className="stepper">
            <button onClick={() => stepPoints(-tick)} disabled={!canEditForecast} aria-label={`меньше на ${pts(tick)}`}>
              −
            </button>
            <input
              id="pts"
              inputMode="decimal"
              className="mono"
              placeholder="—"
              value={pointsDraft}
              disabled={!canEditForecast}
              onChange={(e) => setPointsDraft(e.target.value)}
              onBlur={commitDraft}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            />
            <button onClick={() => stepPoints(tick)} disabled={!canEditForecast} aria-label={`больше на ${pts(tick)}`}>
              +
            </button>
          </div>
        </div>
        <Notice
          items={[
            missingDir &&
              game.stage !== 'round3' && {
                tone: 'warn',
                text: 'Выберите направление — без него нельзя вносить кредиты. В круге 3 направление уже не выбрать.',
              },
            missingDir &&
              game.stage === 'round3' && {
                tone: 'danger',
                text: 'Направление не выбрано, а в круге 3 его уже не выбрать: при фиксации вы выбудете.',
              },
            canEditForecast &&
              !missingDir &&
              game.stage === 'round2' && {
                tone: 'info',
                text: 'В круге 3 направление менять будет нельзя — только число пунктов.',
              },
            canEditForecast &&
              !missingDir &&
              game.stage === 'round3' && {
                tone: 'info',
                text: 'Направление зафиксировано: в круге 3 меняется только число пунктов.',
              },
            pointsRequired &&
              missingPoints && {
                tone: 'danger',
                text: `Укажите число пунктов: без него при фиксации через ${lockLeft} с вы выбываете.`,
              },
          ]}
        />
      </div>

      {/* ── Ставка ── */}
      <div className="ctl">
        <CtlHead title="Ставка">
          <TurnChip game={game} now={now} />
        </CtlHead>
        <TurnLine game={game} now={now} />
        <BetStatus game={game} now={now} folded={folded} allIn={allIn} />
        {a && <ActionBar a={a} game={game} amount={amount} setAmount={setAmount} onFold={doFold} onCheckCall={doCheckCall} onBetRaise={doBetRaise} onAllIn={doAllIn} pending={pending} />}
      </div>

      {/* ── Предварительное действие ── */}
      {!locked && !folded && !allIn && <PreAction game={game} />}

      {confirm === 'fold' && (
        <Dialog
          title="Пас"
          tone="danger"
          confirmText="Спасовать"
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            client.act('fold');
            setConfirm(null);
          }}
        >
          <p>Вы выйдете из розыгрыша. Внесённые {cr(meSeat?.committed ?? 0)} останутся в банке и вернутся только при отмене розыгрыша.</p>
          <label className="check">
            <input
              type="checkbox"
              checked={!settings.confirmFold}
              onChange={(e) =>
                updateSettings((s) => ({
                  ...s,
                  confirmFold: !e.target.checked,
                }))
              }
            />{' '}
            Больше не спрашивать
          </label>
        </Dialog>
      )}
      {confirm === 'allin' && (
        <AllInDialog
          game={game}
          points={allinPoints}
          setPoints={setAllinPoints}
          onClose={() => setConfirm(null)}
          onConfirm={(p) => {
            if (p !== me.forecast.points) client.forecast({ points: p });
            client.act(a?.call?.allIn && !a.allIn ? 'call' : 'allin');
            setConfirm(null);
          }}
        />
      )}
    </section>
  );
}

function CtlHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="ctl-head">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

function Notice({ items }: { items: (false | null | undefined | { tone: 'warn' | 'danger' | 'info'; text: string })[] }) {
  const list = items.filter(Boolean) as { tone: string; text: string }[];
  if (!list.length) return null;
  return (
    <div className="notices">
      {list.map((n) => (
        <p key={n.text} className={`notice ${n.tone}`}>
          {n.text}
        </p>
      ))}
    </div>
  );
}

/** Метка в шапке раздела «Ставка»: чей ход и сколько осталось. */
function TurnChip({ game, now }: { game: GameView; now: number }) {
  const round = ROUND_OF[game.stage];
  if (!round) return null;
  if (game.bettingClosed) return <span className="state">круг {round} · ставки завершены</span>;
  const left = secondsLeft(game.turnEndsAt, now);
  if (game.me.allowed) return <span className="state accent">ваш ход · {left} с</span>;
  const who = game.seats.find((s) => s.playerId === game.toActPlayerId);
  return who ? (
    <span className="state">
      ходит {who.name} · {left} с
    </span>
  ) : null;
}

/** Полоса времени текущего хода под шапкой раздела «Ставка» — та же, что у ходящего в списке участников. */
function TurnLine({ game, now }: { game: GameView; now: number }) {
  const turnMs = useApp((s) => s.info?.turnMs ?? 12_000);
  if (!ROUND_OF[game.stage] || game.bettingClosed || game.turnEndsAt === null || !game.toActPlayerId) return null;
  return <TurnBar endsAt={game.turnEndsAt} turnMs={turnMs} now={now} me={!!game.me.allowed} />;
}

/** Строка состояния: почему сейчас нельзя ставить и когда станет можно. */
function BetStatus({ game, now, folded, allIn }: { game: GameView; now: number; folded: boolean; allIn: boolean }) {
  const round = ROUND_OF[game.stage];
  const stageLeft = mmss(secondsLeft(game.stageEndsAt, now));
  const facts = (
    <dl className="bet-facts">
      <div>
        <dt>Ставка круга</dt>
        <dd className="mono">{game.currentBet ? cr(game.currentBet) : '—'}</dd>
      </div>
      <div>
        <dt>К уравниванию</dt>
        <dd className="mono">{game.me.toCall ? cr(game.me.toCall) : '—'}</dd>
      </div>
      <div>
        <dt>Повышения</dt>
        <dd className="mono">{round ? `${game.raisesLeft} из 1` : '—'}</dd>
      </div>
    </dl>
  );
  let text: string | null = null;
  if (folded) text = 'Вы спасовали — участие в этом розыгрыше завершено.';
  else if (allIn) text = 'Вы в олл-ине: ставок больше не делаете, прогноз можно менять до фиксации.';
  else if (game.stage === 'prep') text = `Первый круг ставок начнётся через ${stageLeft}.`;
  else if (round && game.bettingClosed)
    text =
      round < 3
        ? `Все участники сделали ход. Следующий круг ставок откроется через ${stageLeft} ${NEXT_REVEAL[round]}.`
        : `Все участники сделали ход. Прогнозы будут зафиксированы через ${stageLeft}.`;
  else if (round && !game.me.allowed) text = 'Дождитесь своего хода. Можно заранее выбрать предварительное действие.';
  else if (game.me.allowed && game.raisesLeft === 0 && game.currentBet > 0) text = 'Лимит повышений в этом круге исчерпан — доступны колл или пас.';
  return (
    <>
      {facts}
      {text && <p className="muted-line">{text}</p>}
    </>
  );
}

function ActionBar(props: {
  a: AllowedActions;
  game: GameView;
  amount: number;
  setAmount: (v: number) => void;
  onFold: () => void;
  onCheckCall: () => void;
  onBetRaise: () => void;
  onAllIn: () => void;
  pending: boolean;
}) {
  const { a, game, amount, setAmount } = props;
  const range = a.bet ?? a.raise ?? null;
  const presets = useMemo(() => {
    if (!range) return [];
    const clamp = (v: number) => Math.min(range.max, Math.max(range.min, Math.round(v / 100) * 100));
    return [
      { label: 'Мин', v: range.min },
      { label: '½ банка', v: clamp(game.currentBet + game.pot / 2) },
      { label: 'Банк', v: clamp(game.currentBet + game.pot) },
      { label: 'Макс', v: range.max },
    ];
  }, [range?.min, range?.max, game.pot, game.currentBet]);
  const noDirection = !a.call && !a.bet && !a.raise && !a.allIn;

  return (
    <div className="actions">
      {range && (
        <div className="amount">
          <div className="field-row">
            <label htmlFor="amt">
              <span>{a.bet ? 'Сумма ставки' : 'Повысить до'}</span>
              <small>
                от {cr(range.min)} до {cr(range.max)}
              </small>
            </label>
            <div className="input-suffix">
              <input id="amt" className="mono" type="number" min={range.min / 100} max={range.max / 100} step={1} value={amount / 100} onChange={(e) => setAmount(toMinor(Number(e.target.value)))} />
              <span>кр.</span>
            </div>
          </div>
          <input type="range" min={range.min} max={range.max} step={100} value={amount} onChange={(e) => setAmount(Number(e.target.value))} aria-label="Сумма ставки" />
          <div className="segmented four">
            {presets.map((p) => (
              <button key={p.label} className={amount === p.v ? 'on' : ''} onClick={() => setAmount(p.v)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="action-grid">
        <button className="btn danger-outline" onClick={props.onFold} disabled={props.pending}>
          <span>Пас</span>
          <kbd>F</kbd>
        </button>
        <button className="btn" onClick={props.onCheckCall} disabled={props.pending || (!a.check && !a.call)}>
          <span>{a.check ? 'Чек' : a.call ? `Колл ${cr(a.call.amount)}` : 'Колл'}</span>
          <kbd>C</kbd>
        </button>
        <button className="btn primary" onClick={props.onBetRaise} disabled={props.pending || !range}>
          <span>
            {a.raise || (!a.bet && game.currentBet > 0) ? 'Райз' : 'Ставка'}
            {range ? ` ${cr(amount)}` : ''}
          </span>
          <kbd>B</kbd>
        </button>
        <button className="btn accent-outline" onClick={props.onAllIn} disabled={props.pending || !a.allIn}>
          <span>Олл-ин{a.allIn ? ` ${cr(a.allIn.amount)}` : ''}</span>
          <kbd>A</kbd>
        </button>
      </div>
      {noDirection && <p className="notice warn">Чтобы вносить кредиты, выберите направление прогноза.</p>}
    </div>
  );
}

/** Варианты предварительного действия: подпись кнопки и пояснение (всплывающая подсказка и строка под кнопками). */
const PRE_OPTIONS: { mode: AutoMode; label: string; hint: string }[] = [
  {
    mode: 'none',
    label: 'Нет',
    hint: 'Без предварительного действия: на своём ходу решаете сами.',
  },
  {
    mode: 'checkOnly',
    label: 'Чек',
    hint: 'Чек, если ставки нет. Если кто-то поставил — ход остаётся за вами.',
  },
  {
    mode: 'checkFold',
    label: 'Чек / пас',
    hint: 'Чек, если ставки нет; если кто-то поставил — пас.',
  },
  {
    mode: 'callUpTo',
    label: 'Колл до лимита',
    hint: 'Колл, если к уравниванию не больше лимита; если ставки нет — чек. Ставка выше лимита — решаете сами.',
  },
];

/**
 * Одноразовое действие на ближайший ход (как «чек/пас» в покерных клиентах). Настраивается в любой
 * момент до фиксации; во время собственного хода — на следующий ход.
 */
function PreAction({ game }: { game: GameView }) {
  const auto = game.me.autoAction;
  const [limit, setLimit] = useState<number>(auto.limit ? auto.limit / 100 : game.ante / 100);
  const myTurn = !!game.me.allowed;
  const set = (mode: AutoMode) => client.auto(mode === 'callUpTo' ? { mode, limit: toMinor(limit) } : { mode });
  const current = PRE_OPTIONS.find((o) => o.mode === auto.mode) ?? PRE_OPTIONS[0]!;
  return (
    <div className="ctl">
      <CtlHead title="Предварительное действие">{auto.mode !== 'none' && <span className="state accent">запланировано</span>}</CtlHead>
      <div className="segmented grid2" role="radiogroup" aria-label="Предварительное действие">
        {PRE_OPTIONS.map((o) => (
          <button key={o.mode} role="radio" aria-checked={auto.mode === o.mode} className={auto.mode === o.mode ? 'on' : ''} title={o.hint} onClick={() => set(o.mode)}>
            {o.label}
          </button>
        ))}
      </div>
      {auto.mode === 'callUpTo' && (
        <div className="field-row">
          <label htmlFor="autolim">
            <span>Лимит колла</span>
            <small>больше — решаете сами</small>
          </label>
          <div className="input-suffix">
            <input
              id="autolim"
              className="mono"
              type="number"
              min={1}
              value={limit}
              onChange={(e) => {
                const v = Number(e.target.value);
                setLimit(v);
                if (v > 0) client.auto({ mode: 'callUpTo', limit: toMinor(v) });
              }}
            />
            <span>кр.</span>
          </div>
        </div>
      )}
      <p className="muted-line">
        {current.hint} {auto.mode !== 'none' && (myTurn ? 'Сработает один раз — на вашем следующем ходу.' : 'Сработает один раз — на вашем ближайшем ходу.')}
      </p>
    </div>
  );
}

function AllInDialog(props: { game: GameView; points: string; setPoints: (s: string) => void; onClose: () => void; onConfirm: (p: number) => void }) {
  const { game } = props;
  const meSeat = game.seats.find((s) => s.isMe)!;
  const v = Number(props.points.replace(',', '.'));
  const valid = props.points.trim() !== '' && Number.isFinite(v) && v > 0 && Math.abs(v / game.tick - Math.round(v / game.tick)) < 1e-9;
  const dir = game.me.forecast.direction;
  return (
    <Dialog
      title="Олл-ин"
      tone="danger"
      confirmText={`Внести ${cr(meSeat.stack)}`}
      onClose={props.onClose}
      onConfirm={valid && dir ? () => props.onConfirm(v) : undefined}
      confirmDisabled={!valid || !dir}
    >
      <p>
        Вы вносите весь доступный остаток <b>{cr(meSeat.stack)}</b> и больше не делаете ставок в этом розыгрыше. Прогноз можно менять до общей фиксации.
      </p>
      <dl className="kv">
        <div>
          <dt>Направление</dt>
          <dd className={dir === 'up' ? 'up' : dir === 'down' ? 'down' : ''}>{dir === 'up' ? '▲ выше' : dir === 'down' ? '▼ ниже' : 'не выбрано'}</dd>
        </div>
      </dl>
      <label className="field">
        Предварительное число пунктов (обязательно)
        <input className="mono" inputMode="decimal" value={props.points} onChange={(e) => props.setPoints(e.target.value)} placeholder={`кратно ${pts(game.tick)}`} data-autofocus />
      </label>
      {!valid && props.points !== '' && <p className="notice warn">Строго положительное число, кратное {pts(game.tick)}.</p>}
    </Dialog>
  );
}
